/**
 * Live Stream Player
 *
 * Connects to the server-side WebSocket proxy (/api/camera/ws-stream) which
 * handles camera auth and CGI streaming.
 *
 * Camera sends MJPEG for both streams (streamID=1 is higher resolution,
 * streamID=2 is lower). Frames are displayed in an <img> element.
 *
 * Auto-reconnects on disconnect with exponential backoff.
 * Instantly reconnects when tab becomes visible (bypasses backoff).
 */

export interface LivePlayerOptions {
  /** Container element — player will create <img> inside it */
  container: HTMLDivElement
  wsUrl: string
  isHd: boolean
  onError?: (error: Error) => void
  onConnected?: () => void
}

const RECONNECT_BASE_MS = 2000
const RECONNECT_MAX_MS = 30000

export class LivePlayer {
  private options: LivePlayerOptions
  private ws: WebSocket | null = null
  private stopped = false
  private prevBlobUrl: string | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private reconnectAttempts = 0
  private imgEl: HTMLImageElement | null = null
  private visibilityHandler: (() => void) | null = null

  constructor(options: LivePlayerOptions) {
    this.options = options
  }

  start(): void {
    this.stopped = false
    this.reconnectAttempts = 0
    this.setupElements()
    this.connect()
    this.listenVisibility()
  }

  /** Instantly reconnect when tab becomes visible again (bypasses backoff) */
  private listenVisibility(): void {
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible' && !this.stopped) {
        const dead = !this.ws || this.ws.readyState === WebSocket.CLOSED || this.ws.readyState === WebSocket.CLOSING
        if (dead) {
          if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer)
            this.reconnectTimer = null
          }
          this.reconnectAttempts = 0
          this.connect()
        }
      }
    }
    document.addEventListener('visibilitychange', this.visibilityHandler)
  }

  private setupElements(): void {
    const container = this.options.container
    container.innerHTML = ''

    const img = document.createElement('img')
    img.className = 'w-full h-full object-contain bg-black'
    img.alt = ''
    container.appendChild(img)
    this.imgEl = img
  }

  private connect(): void {
    if (this.stopped) return

    this.ws = new WebSocket(this.options.wsUrl)
    this.ws.binaryType = 'arraybuffer'

    this.ws.onopen = () => {
      if (!this.stopped) {
        this.reconnectAttempts = 0
        this.options.onConnected?.()
      }
    }

    this.ws.onmessage = (event) => {
      if (this.stopped) return

      if (event.data instanceof ArrayBuffer) {
        const blob = new Blob([event.data], { type: 'image/jpeg' })
        const url = URL.createObjectURL(blob)
        if (this.prevBlobUrl) URL.revokeObjectURL(this.prevBlobUrl)
        this.prevBlobUrl = url
        if (this.imgEl) this.imgEl.src = url
        return
      }

      // Text frames = error/status messages from server
      if (typeof event.data === 'string') {
        try {
          const msg = JSON.parse(event.data)
          if (msg.error && !this.stopped) {
            this.options.onError?.(new Error(msg.error))
          }
        } catch {}
      }
    }

    this.ws.onclose = () => {
      if (!this.stopped) {
        this.scheduleReconnect()
      }
    }

    this.ws.onerror = () => {
      // onclose will fire after onerror, so reconnect is handled there
    }
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.reconnectTimer) return

    this.reconnectAttempts++
    const delay = Math.min(RECONNECT_BASE_MS * Math.pow(1.5, this.reconnectAttempts - 1), RECONNECT_MAX_MS)

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      if (!this.stopped) this.connect()
    }, delay)
  }

  stop(): void {
    this.stopped = true
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler)
      this.visibilityHandler = null
    }
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.ws) {
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close()
      }
      this.ws = null
    }
    if (this.prevBlobUrl) {
      URL.revokeObjectURL(this.prevBlobUrl)
      this.prevBlobUrl = null
    }
    this.imgEl = null
  }
}
